import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type {
  ColourStop,
  SchematicAnim,
  SchematicOverlayWidget,
} from '../widgets';
import type { IPointBindingSource, PointValue } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { effect } from '@preact/signals';
import { formatNumber } from '../../format/leafValue';
import './SchematicOverlayRenderer.css';

interface Props {
  widget: SchematicOverlayWidget;
  source: IPointBindingSource;
}

/**
 * Concept §8 Stufe 1 + Stufe 2 in one widget: the customer's SVG background
 * plus a list of anim entries that drive `fill`, `rotate`, `visible`, `text`
 * and `path-length` on IDed elements inside the SVG. The SVG is sanitised at
 * upload time (HmiAssets slice) so we inline it via `innerHTML` and query for
 * anim target elements once after mount.
 *
 * Signal lifecycle mirrors GaugeRenderer: one `pointSignal` per anim, released
 * on unmount. A shared effect subscribes to every signal and applies the SVG
 * attribute updates in a rAF batch so a schema with dozens of anims does not
 * trigger a Preact re-render storm.
 */
export function SchematicOverlayRenderer({ widget, source }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // One pointSignal per anim, held for the lifetime of the widget instance.
  // useMemo so we don't re-subscribe on every render; explicit release() on
  // unmount to close the underlying stream.
  const signals = useMemo(
    () =>
      widget.anims.map((anim) => ({
        anim,
        handle: pointSignal(source, anim.valuePoint),
      })),
    // widget.anims identity is expected stable per widget instance; if the
    // caller swaps the widget, Preact unmounts + remounts us and we get fresh
    // subscriptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widget.id],
  );
  useEffect(
    () => () => {
      for (const s of signals) s.handle.release();
    },
    [signals],
  );

  // Fetch the SVG once per widget instance. Two source paths: uploaded asset
  // (new) and legacy embedded schematic. Widget-level validator enforces that
  // exactly one is set, but we defensively fall through to an error tile.
  useEffect(() => {
    const abort = new AbortController();
    const source = resolveSvgUrl(widget);
    if (source.kind === 'obsolete') {
      setLoadError(
        'Der Hintergrund hängt an einer SVG-Id. Dieser Weg wird nicht mehr '
        + 'bedient; das Schema gehört in den Assetspeicher und wird im Editor '
        + 'unter "Asset" gewählt.',
      );
      return;
    }
    const url = source.kind === 'url' ? source.url : null;
    if (!url) {
      setLoadError('Kein Schema hinterlegt. Im Editor unter "Asset" eines wählen.');
      return () => abort.abort();
    }
    setLoadError(null);
    setSvgMarkup(null);
    (async () => {
      try {
        const resp = await fetch(url, { signal: abort.signal });
        if (!resp.ok) {
          setLoadError(`SVG konnte nicht geladen werden (HTTP ${resp.status}).`);
          return;
        }
        const text = await resp.text();
        setSvgMarkup(text);
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setLoadError(
          `SVG-Ladefehler: ${(err as Error).message ?? 'unbekannt'}`,
        );
      }
    })();
    return () => abort.abort();
  }, [widget.svgId, widget.assetRef?.assetId]);

  // Resolve element refs once after SVG mount and wire the anim-apply effect.
  // Doing the querySelector-once-then-cache dance keeps the per-tick cost to a
  // single attribute write per anim, no matter how big the SVG is.
  useEffect(() => {
    if (!svgMarkup || !containerRef.current) return;
    const root = containerRef.current;
    // Inline the sanitised markup.
    root.innerHTML = svgMarkup;

    const resolved = signals.map(({ anim, handle }) => {
      const el = root.querySelector<Element>(`#${cssEscape(anim.elementId)}`);
      if (!el) {
        console.warn('[SchematicOverlayRenderer] element not found', {
          widgetId: widget.id,
          elementId: anim.elementId,
        });
      }
      return { anim, handle, element: el };
    });

    const dispose = effect(() => {
      for (const { anim, handle, element } of resolved) {
        if (!element) continue;
        const pv = handle.value.value;
        applyAnim(element, anim, pv);
      }
    });

    return () => {
      dispose();
      // Do not clear innerHTML here; the outer effect re-runs on svgMarkup
      // change and will replace it. Keeping the tree stable avoids a flash
      // between fetches.
    };
  }, [svgMarkup, signals, widget.id]);

  // Full cleanup: on component unmount, drop the inline SVG so the DOM is
  // clean if the container is recycled by the layout.
  useEffect(
    () => () => {
      if (containerRef.current) containerRef.current.innerHTML = '';
    },
    [],
  );

  // Staleness rollup: read every signal so the effect re-runs on any change.
  const qualityStates = signals.map((s) => s.handle.value.value);
  const totalCount = qualityStates.length;
  const badCount = qualityStates.filter(
    (v) => isDegraded(v),
  ).length;
  const anyStale = badCount > 0;
  const allBad = totalCount > 0 && badCount === totalCount;

  if (loadError) {
    return (
      <div class="hv-schematic hv-schematic--error" role="alert">
        <strong>Schema nicht verfügbar</strong>
        <span>{loadError}</span>
        {widget.label && <span class="hv-schematic__label">{widget.label}</span>}
      </div>
    );
  }

  if (allBad) {
    return (
      <div class="hv-schematic hv-schematic--novalues" role="status">
        <div ref={containerRef} class="hv-schematic__svg hv-schematic__svg--dim" />
        <div class="hv-schematic__novalues-tile">
          <strong>Keine Live-Werte</strong>
          <span>SVG geladen, aber keine Live-Werte verfügbar.</span>
        </div>
        {widget.label && <div class="hv-schematic__label">{widget.label}</div>}
      </div>
    );
  }

  return (
    <div class={`hv-schematic${anyStale ? ' hv-schematic--stale' : ''}`}>
      <div ref={containerRef} class="hv-schematic__svg" />
      {anyStale && (
        <span class="hv-schematic__stale-badge" role="status">
          Werte veraltet
        </span>
      )}
      {widget.label && <div class="hv-schematic__label">{widget.label}</div>}
    </div>
  );
}

// ---------- URL resolution ----------

/**
 * Ergebnis der Quellenaufloesung. Drei Faelle und nicht zwei, weil "es gibt
 * keinen Weg dorthin" etwas anderes ist als "es ist nichts eingetragen".
 */
type SvgSource =
  | { kind: 'url'; url: string }
  | { kind: 'none' }
  | { kind: 'obsolete' };

function resolveSvgUrl(widget: SchematicOverlayWidget): SvgSource {
  if (widget.assetRef?.assetId) {
    return {
      kind: 'url',
      url: `/api/hmi-assets/${encodeURIComponent(widget.assetRef.assetId)}/content`,
    };
  }
  if (widget.svgId) {
    // `svgId` sollte einmal ueber `GET /api/hmi/svg/{svgId}` bedient werden.
    // **Diese Route ist nie entstanden**, am 23.09.2026 im ganzen Server
    // nachgesucht: der Name steht nur in zwei Kommentaren und hier. Das Feld
    // hat also nie funktioniert, und ein Widget, das es benutzt, holte sich
    // einen 404 und zeigte "SVG konnte nicht geladen werden" - eine Meldung,
    // die nach einem Betriebsproblem aussieht statt nach einem Feld, das es
    // nicht gibt. Der gangbare Weg ist der Assetspeicher.
    return { kind: 'obsolete' };
  }
  return { kind: 'none' };
}

// ---------- Anim application ----------

function applyAnim(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  switch (anim.target) {
    case 'fill':
      applyFill(element, anim, pv);
      return;
    case 'rotate':
      applyRotate(element, anim, pv);
      return;
    case 'visible':
      applyVisible(element, anim, pv);
      return;
    case 'text':
      applyText(element, anim, pv);
      return;
    case 'pathLength':
      applyPathLength(element, anim, pv);
      return;
  }
}

function applyFill(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  const colour = resolveFillColour(anim, pv?.value);
  if (colour == null) return;
  if (element instanceof SVGElement) {
    element.setAttribute('fill', colour);
  } else if (element instanceof HTMLElement) {
    element.style.fill = colour;
    element.style.backgroundColor = colour;
  }
}

function resolveFillColour(anim: SchematicAnim, raw: unknown): string | null {
  const ramp = anim.colourRamp ?? [];
  if (ramp.length === 0) return null;
  // Enum path: non-numeric or when the ramp `at` values look like enum
  // ordinals (integer, no interpolation). We hit that path when the value is
  // string/boolean, or when there's an exact `at` equal to the numeric value.
  const numeric = coerceNumber(raw);
  if (numeric == null) {
    const asKey = raw == null ? null : String(raw);
    const stop = ramp.find((s) => String(s.at) === asKey);
    return stop ? stop.colour : ramp[0].colour;
  }
  // Try exact enum-ordinal hit first for the enum→colour mapping case.
  const exact = ramp.find((s) => s.at === numeric);
  if (exact) return exact.colour;
  return interpolateRamp(ramp, numeric, anim.min ?? undefined, anim.max ?? undefined);
}

function interpolateRamp(
  ramp: ColourStop[],
  value: number,
  min?: number,
  max?: number,
): string {
  // Support both absolute-value stops and normalised 0..1 stops. Callers pin
  // this by choosing their `at` values against widget Min/Max.
  const clamped =
    min != null && max != null && max > min
      ? clamp(value, min, max)
      : value;
  const sorted = [...ramp].sort((a, b) => a.at - b.at);
  if (clamped <= sorted[0].at) return sorted[0].colour;
  if (clamped >= sorted[sorted.length - 1].at)
    return sorted[sorted.length - 1].colour;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (clamped >= a.at && clamped <= b.at) {
      const t = (clamped - a.at) / (b.at - a.at);
      return lerpColour(a.colour, b.colour, t);
    }
  }
  return sorted[sorted.length - 1].colour;
}

function applyRotate(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  const raw = coerceNumber(pv?.value);
  if (raw == null) return;
  // Two modes: angle (Min/Max in degrees) and speed (Min/Max as % → dps).
  // If Max <= 360 we treat it as a static angle. Otherwise the value is
  // interpreted as speed % → we run a CSS animation with a duration derived
  // from an interpolated deg/s.
  const min = anim.min ?? 0;
  const max = anim.max ?? 100;
  if (max <= 360 && min >= -360) {
    // Static angle mode.
    setRotationAngle(element, raw);
    return;
  }
  // Speed mode: value fraction of 0..1 mapped onto 0..1080 deg/s.
  const frac = clamp((raw - min) / (max - min || 1), 0, 1);
  const dps = frac * 1080;
  setRotationSpeed(element, dps);
}

function setRotationAngle(element: Element, degrees: number): void {
  if (!(element instanceof SVGElement || element instanceof HTMLElement))
    return;
  const centre = elementRotationCentre(element);
  const t = `rotate(${degrees.toFixed(2)} ${centre.x} ${centre.y})`;
  if (element instanceof SVGElement) {
    element.setAttribute('transform', mergeTransform(element, t));
  } else {
    element.style.transform = `rotate(${degrees.toFixed(2)}deg)`;
    element.style.transformOrigin = 'center';
  }
}

function setRotationSpeed(element: Element, degreesPerSecond: number): void {
  if (!(element instanceof SVGElement || element instanceof HTMLElement))
    return;
  const el = element as unknown as HTMLElement;
  if (degreesPerSecond <= 0.1) {
    el.style.animation = 'none';
    return;
  }
  const durationSec = 360 / degreesPerSecond;
  el.style.animationName = 'hv-schematic-spin';
  el.style.animationDuration = `${durationSec.toFixed(3)}s`;
  el.style.animationTimingFunction = 'linear';
  el.style.animationIterationCount = 'infinite';
  el.style.transformOrigin = 'center';
}

function applyVisible(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  const target = anim.visibleWhen;
  const isVisible =
    target === undefined
      ? pv?.value !== false && pv?.value != null
      : valuesEqual(target, pv?.value);
  if (element instanceof SVGElement) {
    element.setAttribute('visibility', isVisible ? 'visible' : 'hidden');
  } else if (element instanceof HTMLElement) {
    element.style.visibility = isVisible ? 'visible' : 'hidden';
  }
}

function applyText(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  const text = formatText(anim.textFormat, pv?.value);
  // Prefer a <tspan> child if present (typical for Inkscape/Illustrator text),
  // otherwise write the element's textContent directly.
  const tspan = element.querySelector('tspan');
  if (tspan) tspan.textContent = text;
  else element.textContent = text;
}

function formatText(template: string | undefined, raw: unknown): string {
  const asString = raw == null ? '' : String(raw);
  if (!template) return asString;
  // Support {value} and {value:N} for N decimal places.
  return template.replace(/\{value(?::(\d+))?\}/g, (_m, dec) => {
    if (dec != null) {
      const n = coerceNumber(raw);
      if (n != null) return formatNumber(n, parseInt(dec, 10));
    }
    return asString;
  });
}

function applyPathLength(
  element: Element,
  anim: SchematicAnim,
  pv: PointValue | null,
): void {
  const raw = coerceNumber(pv?.value);
  if (raw == null) return;
  const min = anim.min ?? 0;
  const max = anim.max ?? 100;
  const frac = clamp((raw - min) / (max - min || 1), 0, 1);
  // getTotalLength is only defined on SVGGeometryElement (path, line, polyline,
  // polygon, rect, circle, ellipse). Guard for the rare case someone points
  // a pathLength anim at a <g> or other non-geometric node.
  const geo = element as SVGGeometryElement & {
    getTotalLength?: () => number;
  };
  let total = 0;
  try {
    total = geo.getTotalLength ? geo.getTotalLength() : 0;
  } catch {
    total = 0;
  }
  if (total <= 0) {
    // Fallback: fake it via `pathLength` attribute + fractional dasharray on
    // a 0..1 domain. Works for elements that support the SVG2 `pathLength`
    // hint even without a real geometric length.
    element.setAttribute('pathLength', '1');
    element.setAttribute('stroke-dasharray', `${frac} ${1 - frac}`);
    return;
  }
  const filled = total * frac;
  const unfilled = total - filled;
  element.setAttribute('stroke-dasharray', `${filled} ${unfilled}`);
}

// ---------- helpers ----------

function elementRotationCentre(el: SVGElement | HTMLElement): {
  x: number;
  y: number;
} {
  if (el instanceof SVGGraphicsElement) {
    try {
      const bb = el.getBBox();
      return { x: bb.x + bb.width / 2, y: bb.y + bb.height / 2 };
    } catch {
      return { x: 0, y: 0 };
    }
  }
  return { x: 0, y: 0 };
}

function mergeTransform(el: SVGElement, next: string): string {
  // Preserve existing translate/scale from the source SVG: append the rotate
  // to whatever is already there instead of stomping it.
  const existing = el.getAttribute('data-hv-base-transform');
  if (existing != null) return `${existing} ${next}`.trim();
  const original = el.getAttribute('transform') ?? '';
  if (original) {
    el.setAttribute('data-hv-base-transform', original);
    return `${original} ${next}`;
  }
  el.setAttribute('data-hv-base-transform', '');
  return next;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Interpolate between two colours in sRGB space. Accepts #rgb, #rrggbb, and
 *  named colours (via a lazily-built canvas probe). Returns the target colour
 *  string as `rgb(r, g, b)`. Falls back to `to` on parse failure. */
function lerpColour(from: string, to: string, t: number): string {
  const a = parseColour(from);
  const b = parseColour(to);
  if (!a || !b) return t < 0.5 ? from : to;
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

const colourCache = new Map<string, [number, number, number] | null>();

function parseColour(input: string): [number, number, number] | null {
  const cached = colourCache.get(input);
  if (cached !== undefined) return cached;
  let out: [number, number, number] | null = null;
  const hex = input.trim();
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    out = [
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16),
      parseInt(hex[3] + hex[3], 16),
    ];
  } else if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
    out = [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  } else {
    const m = hex.match(/^rgba?\(([^)]+)\)$/i);
    if (m) {
      const parts = m[1].split(',').map((p) => parseFloat(p.trim()));
      if (parts.length >= 3 && parts.every((n) => Number.isFinite(n))) {
        out = [parts[0], parts[1], parts[2]];
      }
    }
  }
  colourCache.set(input, out);
  return out;
}

/** Escape a raw id for use in a CSS selector. Uses the browser's built-in
 *  `CSS.escape` when available (all modern targets) and falls back to a
 *  conservative manual escape otherwise. */
function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id);
  }
  return id.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
